import SvgLoader from 'src/components/Common/SvgLoader';
import Link from '../../Link';
import CallToAction from '../CallToAction';
import { RichText } from '../RichText';
import { SimpleCardLinkProps, SimpleCardProps, SimpleCardsComponentProps } from './types';

const SimpleCardLink = ({ className, link, header, onClick }: SimpleCardLinkProps) => {
  if (typeof link === 'object') {
    if (!link.href) {
      return <span className={className}>{header}</span>;
    }
    return (
      <Link value={link} className={className}>
        {header}
      </Link>
    );
  } else if (typeof link === 'string') {
    return (
      <a
        className={className}
        href={link}
        onClick={() => {
          onClick?.();
        }}
      >
        {header}
      </a>
    );
  }
  return null;
};

// this component is intended to be list items, so should be <ul><li><SimpleCard /></li></ul>
export const SimpleCard = ({
  link,
  icon,
  header,
  description,
  disabled = false,
  onClick,
  boldHeader = false,
}: SimpleCardProps) => (
  <div
    data-testid={disabled ? 'simpleCard-disabled' : ''}
    className={`relative flex p-16 rounded-md border border-gray  ${
      disabled
        ? 'bg-gray-lighter grayscale'
        : 'bg-white sm:hover:scale-102 transition-transform duration-300 ease-in-out'
    }`}
  >
    {/* electron svg icon classes: "icon-28 sm:icon-36"  */}
    {typeof icon !== 'undefined' && (
      <div className="flex-shrink-0 mr-12 sm:mr-16 sm:flex sm:items-center">{icon}</div>
    )}

    <div className="flex-grow flex flex-col justify-center">
      <span className="pt-2">
        {disabled && (
          <span className={`text-lg text-teal-darker${boldHeader ? ' font-bold' : ''}`}>
            {header}
          </span>
        )}
        {!disabled && (
          <SimpleCardLink
            link={link}
            onClick={onClick}
            header={header}
            className={`text-lg text-teal-darker${
              boldHeader ? ' font-bold' : ''
            } before:absolute before:-inset-px before:rounded-md before:shadow-sm before:opacity-0 sm:hover:before:opacity-100 before:transition-opacity before:duration-300 before:ease-in-out`}
          />
        )}
      </span>
      {/* changed span to div (which doesn't need block class) because description is set as rich text in Sitecore and could contain elements that can't go in a span */}
      <div>{description}</div>
    </div>
    <div className="flex-shrink-0 ml-16 flex items-center">
      <SvgLoader color="text-blue" name="Arrow" size={16} />
    </div>
  </div>
);

const SimpleCardsComponent = ({ CTA, cards, prompt }: SimpleCardsComponentProps) => {
  return (
    <CallToAction {...CTA}>
      <div className="mx-auto max-w-3xl">
        {!!prompt && (
          <RichText value={prompt} className="text-xl text-gray-darker text-center mb-24" />
        )}
        <ul className="flex flex-col gap-y-16 max-w-md mx-auto">
          {cards?.map((card, i) => {
            if (card.hideCard) {
              return null;
            }
            const icon = card.iconSrc ? (
              <img src={card.iconSrc} alt="" className="icon-28 sm:icon-36" />
            ) : undefined;
            const description = card.description ? <RichText value={card.description} /> : '';
            return (
              <li key={i} className="w-full">
                <SimpleCard
                  icon={icon}
                  link={card.link}
                  header={card.title}
                  description={description}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </CallToAction>
  );
};

export default SimpleCardsComponent;
